import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiUser as User, FiMail as Mail, FiSun as Sun, FiMoon as Moon } from 'react-icons/fi';
import { apiCall } from '../services/realApi';

interface UserApprovalInfo {
  name: string;
  email: string;
}

const ApproveUser: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserApprovalInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);

  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!token) {
        setError('Invalid or missing approval token');
        setLoading(false);
        return;
      }

      try {
        const data = await apiCall(`/user-approvals/${token}`, { requireAuth: false });
        setUserInfo(data);
      } catch (err: unknown) {
        console.error('Error fetching user info:', err);
        setError(err instanceof Error ? err.message : 'Failed to load user information');
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [token]);

  const handleDecision = async (approvalDecision: 'approve' | 'reject') => {
    setProcessing(true);
    setError(null);

    try {
      await apiCall('/user-approvals/approve', {
        method: 'POST',
        requireAuth: false,
        body: JSON.stringify({
          token,
          decision: approvalDecision
        })
      });

      setDecision(approvalDecision);
      setSuccess(true);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: unknown) {
      console.error('Error processing approval:', err);
      setError(err instanceof Error ? err.message : 'Failed to process approval');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading user information...</p>
        </div>
      </div>
    );
  }

  let approvalContent: React.ReactElement | null = null;
  if (error) {
    approvalContent = (
      <div className="text-center">
        <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
          <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{error}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Go to Home
        </button>
      </div>
    );
  } else if (success) {
    approvalContent = (
      <div className="text-center">
        <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
          decision === 'approve'
            ? 'bg-green-100 dark:bg-green-900/20'
            : 'bg-red-100 dark:bg-red-900/20'
        }`}>
          {decision === 'approve' ? (
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
          )}
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {decision === 'approve' ? 'User Approved!' : 'User Rejected'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {decision === 'approve'
            ? `${userInfo!.name} has been granted access and will receive a welcome email.`
            : `${userInfo!.name} has been notified that their request was not approved.`}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">
          Redirecting to home...
        </p>
      </div>
    );
  } else if (userInfo) {
    approvalContent = (
      <div>
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
            <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            A new user has requested access to your organization
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Name</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{userInfo.name}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{userInfo.email}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => handleDecision('approve')}
            disabled={processing}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5" />
            <span>{processing ? 'Processing...' : 'Approve User'}</span>
          </button>
          <button
            onClick={() => handleDecision('reject')}
            disabled={processing}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle className="w-5 h-5" />
            <span>{processing ? 'Processing...' : 'Reject User'}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors">
      {/* Theme Toggle Button */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors"
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <img
            src={isDarkMode ? "/lens-logo-dark.png" : "/lens-logo-light.png"}
            alt="Aivilo Lens"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="text-center text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          User Approval Request
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 transition-colors">
          {approvalContent}
        </div>
      </div>
    </div>
  );
};

export default ApproveUser;
